import {
  InnerContentItem,
  OuterContentItem,
  PrimaryTag,
} from "@certn/cascadia-react";
import { ArrayControlProps, JsonSchema4, JsonSchema7 } from "@jsonforms/core";
import { Trans, useTranslation } from "react-i18next";
import { Gap } from "./types";
import { useGapTitle } from "./hooks";
import { ActionList, ActionListItem, Link, Stack } from "@certn/cascadia";
import { dataToFile } from "../../utils";
import { formatDate } from "@certn/utils";
import { tryOpenDocumentInNewTab } from "../upload-control/utils";
import { useApplicationFormContext } from "../../ApplicationFormContext";
import { css } from "@emotion/css";

export function GapHistorySummaryControl(props: ArrayControlProps) {
  const gap_reference_schema: JsonSchema4 | JsonSchema7 | undefined =
    props.schema ? props.schema.properties?.["gap_reference"] : undefined;

  const items = props.schema?.properties?.["gap_documentation"]?.items;
  const gap_documentation_schema:
    | Record<string, JsonSchema4 | JsonSchema7>
    | undefined = items && !Array.isArray(items) ? items.properties : undefined;

  return (
    <>
      {props.data
        ?.sort((a: Gap) => (a.is_current ? -1 : a.end_date ? 1 : -1))
        .map((gap: Gap, index: number) => (
          <GapSummaryItem
            key={index}
            gap={gap}
            index={index}
            gap_reference_schema={gap_reference_schema}
            gap_documentation_schema={gap_documentation_schema}
          />
        ))}
    </>
  );
}

function GapSummaryItem({
  gap,
  index,
  gap_reference_schema,
  gap_documentation_schema,
}: {
  gap: Gap;
  index: number;
  gap_reference_schema?: JsonSchema4 | JsonSchema7;
  gap_documentation_schema?: Record<string, JsonSchema4 | JsonSchema7>;
}) {
  const { t } = useTranslation();
  const { downloadUrl } = useApplicationFormContext();
  const title = useGapTitle(gap);
  const gap_documentation = gap.gap_documentation;

  const getOptionLabel = (
    key: "relationship_length" | "relationship",
    value?: string
  ): string => {
    if (!gap_reference_schema) {
      return value ?? "-";
    }

    const oneOf = gap_reference_schema.properties?.[key].oneOf || [];

    const option = oneOf.find((option) => option.const === value);

    return option?.title ?? "-";
  };

  const getDocumentTypeLabel = (value: string) => {
    if (!gap_documentation_schema) {
      return value ?? "-";
    }

    const oneOf = gap_documentation_schema?.document_type.oneOf || [];

    return oneOf.find((t) => t.const === value)?.title ?? value;
  };

  return (
    <OuterContentItem
      title={title}
      tag={
        gap.is_current && (
          <PrimaryTag type="subtle">
            <Trans>Current</Trans>
          </PrimaryTag>
        )
      }
      testID={`gap-summary-${index + 1}`}
    >
      {gap.reason && (
        <InnerContentItem
          label={t("Reason")}
          testID={`gap-summary-reason-${index + 1}`}
        >
          {gap.reason}
        </InnerContentItem>
      )}
      {gap.gap_reference && (
        <div>
          <InnerContentItem
            label={t("Referee name")}
            testID={`gap-summary-referee-name-${index + 1}`}
          >
            {gap.gap_reference.referee_name}
          </InnerContentItem>
          <InnerContentItem
            label={t("Relationship to referee")}
            testID={`gap-summary-relationship-referee-${index + 1}`}
          >
            {getOptionLabel("relationship", gap.gap_reference.relationship)}
          </InnerContentItem>
          <InnerContentItem
            label={t("Referee job title")}
            testID={`gap-summary-referee-job-title-${index + 1}`}
          >
            {gap.gap_reference.job_title}
          </InnerContentItem>
          <InnerContentItem
            label={t("How long have you known them?")}
            testID={`gap-summary-years-known-referee-${index + 1}`}
          >
            {getOptionLabel(
              "relationship_length",
              gap.gap_reference.relationship_length
            )}
          </InnerContentItem>
          <InnerContentItem
            label={t("Referee phone number")}
            testID={`gap-summary-referee-phone-${index + 1}`}
          >
            {gap.gap_reference.phone_number}
          </InnerContentItem>
          <InnerContentItem
            label={t("Referee email")}
            testID={`gap-summary-referee-email-${index + 1}`}
          >
            {gap.gap_reference.email}
          </InnerContentItem>
        </div>
      )}
      {gap_documentation && (
        <InnerContentItem
          label={t("Supporting documentation")}
          testID={`gap-summary-gap-documentation-${index + 1}`}
        >
          <div style={{ paddingTop: 6 }}>
            <ActionList
              hideBorder
              children={
                <Stack gap="space-2">
                  {gap_documentation.map((document) => {
                    const file = dataToFile(document.gap_document_file);

                    return (
                      <ActionListItem
                        key={document.gap_document_file}
                        title={file?.name}
                        className={css({
                          border: "1px solid var(--border-base)",
                          borderRadius: "var(--space-1)",
                        })}
                        trailing={
                          <Link
                            textColor="text-weak"
                            testID="download-file-link"
                            textStyle="global/body-secondary"
                            onPress={() =>
                              tryOpenDocumentInNewTab(
                                downloadUrl,
                                document.gap_document_file
                              )
                            }
                          >
                            {t("Download")}
                          </Link>
                        }
                        children={`${getDocumentTypeLabel(document.document_type)} • ${formatDate(document.document_date, "MMM dd, yyyy")}`}
                      />
                    );
                  })}
                </Stack>
              }
            />
          </div>
        </InnerContentItem>
      )}
    </OuterContentItem>
  );
}
